import React, { useState, useEffect, useCallback, useRef } from 'react';
import DatePicker from 'react-datepicker';
import { useBooking } from '../context/BookingContext';
import { apiService } from '../services/apiService';
import "react-datepicker/dist/react-datepicker.css";

const CustomDatePicker = ({ onInventoryStatusChange }) => {
  const { state, dispatch } = useBooking();
  const [isMobile, setIsMobile] = useState(false);
  const [inventoryStatus, setInventoryStatus] = useState({});
  const [isLoadingInventory, setIsLoadingInventory] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  
  const isMountedRef = useRef(true);
  const currentHotelIdRef = useRef(state.searchState.hotelId);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getMinDate = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  const formatDateForAPI = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const getTwoMonthRange = (startDate) => {
    const start = new Date(startDate);
    start.setDate(1);
    
    const end = new Date(start);
    end.setMonth(end.getMonth() + 2);
    end.setDate(0);
    
    return {
      start: formatDateForAPI(start),
      end: formatDateForAPI(end),
      startDate: start,
      endDate: end
    };
  };

  const loadInventoryRange = useCallback(async (baseDate) => {
    const hotelId = state.searchState.hotelId;
    if (!hotelId) return;

    const { start, end, endDate } = getTwoMonthRange(baseDate);

    const minDate = getMinDate();
    if (endDate < minDate) {
      console.log(`📅 Range ${start} to ${end} before min date, skipping`);
      return;
    }

    try {
      setIsLoadingInventory(true);
      console.log(`🔍 Loading inventory for hotel ${hotelId}, range: ${start} to ${end}`);

      const response = await apiService.checkInventoryAvailability(
        hotelId,
        start,
        end
      );
      
      if (isMountedRef.current && String(currentHotelIdRef.current) === String(hotelId) && response.success && response.data && response.data.dates) {
        console.log(`✅ Loaded ${Object.keys(response.data.dates).length} dates for hotel ${hotelId}`);
        
        // Update inventory status with new data (merge with existing)
        setInventoryStatus(prev => {
          const newStatus = { ...prev, ...response.data.dates };
          
          // Notify parent after state update
          if (onInventoryStatusChange) {
            // Use setTimeout to ensure this runs after render cycle
            setTimeout(() => {
              if (isMountedRef.current) {
                onInventoryStatusChange(newStatus);
              }
            }, 0);
          }
          
          return newStatus;
        });
      }
    } catch (error) {
      console.error(`❌ Failed to load inventory for hotel ${hotelId}, range ${start} to ${end}:`, error);
    } finally {
      if (isMountedRef.current) {
        setIsLoadingInventory(false);
      }
    }
  }, [state.searchState.hotelId, onInventoryStatusChange]);

  const handleCalendarOpen = useCallback(() => {
    setCalendarOpen(true);
    
    if (!state.searchState.hotelId) return;
    
    console.log('📆 Calendar opened, loading initial range for hotel:', state.searchState.hotelId);
    
    // Always load current month when calendar opens
    const today = new Date();
    loadInventoryRange(today);
    
  }, [state.searchState.hotelId, loadInventoryRange]);

  const handleCalendarClose = useCallback(() => {
    setCalendarOpen(false);
  }, []);

  const handleMonthChange = (date) => {
    console.log('📆 Month changed to:', date);
    
    if (!state.searchState.hotelId) return;
    
    const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    
    // Load the range for the new month (always fetch, no skipping)
    loadInventoryRange(firstDayOfMonth);
    
    // Also pre-load next month for better UX
    const nextRangeBase = new Date(firstDayOfMonth);
    nextRangeBase.setMonth(nextRangeBase.getMonth() + 1);
    loadInventoryRange(nextRangeBase);
  };

  // Reset when hotel changes - clear ALL data for new hotel
  useEffect(() => {
    const newHotelId = state.searchState.hotelId;
    const oldHotelId = currentHotelIdRef.current;
    
    if (!newHotelId) return;
    
    console.log(`🏨 Hotel changed from ${oldHotelId} to ${newHotelId}, clearing inventory status`);
    
    // Update ref
    currentHotelIdRef.current = newHotelId;
    
    // COMPLETELY CLEAR inventory status for new hotel
    setInventoryStatus({});
    
    // If calendar is open, load data for new hotel
    if (calendarOpen) {
      console.log(`📆 Calendar is open, loading data for new hotel ${newHotelId}`);
      const today = new Date();
      loadInventoryRange(today);
    }
    
  }, [state.searchState.hotelId, calendarOpen, loadInventoryRange]);

  const renderDayContents = (day, date) => {
    const dateStr = formatDateForAPI(date);
    const status = inventoryStatus[dateStr];
    const isUnavailable = status && !status.has_available_inventory;
    
    return (
      <div className="relative flex items-center justify-center">
        <span className={isUnavailable ? 'text-red-500' : ''}>{day}</span>
        {isUnavailable && (
          <div className="absolute -top-1 -right-1">
            <svg 
              className="w-3.5 h-3.5 text-red-500" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor" 
              strokeWidth="2.5"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        )}
      </div>
    );
  };

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    
    const normalizeDate = (date) => {
      if (!date) return null;
      const normalized = new Date(date);
      normalized.setHours(12, 0, 0, 0);
      return normalized;
    };

    const normalizedStart = normalizeDate(start);
    const normalizedEnd = normalizeDate(end);
    
    dispatch({ type: 'CLEAR_CART' });
    dispatch({
      type: 'UPDATE_SEARCH_STATE',
      payload: { 
        checkin: normalizedStart, 
        checkout: normalizedEnd 
      }
    });

    if (normalizedStart && normalizedEnd) {
      setTimeout(() => {
        dispatch({ type: 'PERFORM_SEARCH', payload: {} });
      }, 400);
    }
  };

  const formatDateForDisplay = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { 
      day: '2-digit', 
      month: 'short', 
      year: '2-digit' 
    });
  };

  // Check if selected date range has any unavailable dates
  const hasUnavailableDatesInRange = () => {
    if (!state.searchState.checkin || !state.searchState.checkout || Object.keys(inventoryStatus).length === 0) {
      return false;
    }
    
    const start = new Date(state.searchState.checkin);
    const end = new Date(state.searchState.checkout);
    let current = new Date(start);
    
    while (current <= end) {
      const dateStr = formatDateForAPI(current);
      const status = inventoryStatus[dateStr];
      if (status && !status.has_available_inventory) {
        return true;
      }
      current.setDate(current.getDate() + 1);
    }
    return false;
  };

  const LuxuryInput = React.forwardRef(({ value, onClick }, ref) => {
    const displayStart = state.searchState.checkin 
      ? formatDateForDisplay(state.searchState.checkin)
      : 'Select Date';
    
    const displayEnd = state.searchState.checkout 
      ? formatDateForDisplay(state.searchState.checkout)
      : 'Select Date';

    const showWarning = hasUnavailableDatesInRange();

    return (
      <div 
        ref={ref}
        onClick={onClick}
        className={`flex items-center bg-white border rounded-xl h-12 hover:border-gray-400 transition-all cursor-pointer shadow-sm divide-x divide-gray-100 md:min-w-[340px] w-full ${
          showWarning ? 'border-red-300 bg-red-50' : 'border-gray-200'
        }`}
      >
        <div className="pl-4 pr-1">
          {isLoadingInventory ? (
            <div className="w-5 h-5 border-2 border-gray-200 border-t-brand-accent rounded-full animate-spin"></div>
          ) : showWarning ? (
            <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          ) : (
            <svg className="w-5 h-5 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          )}
        </div>
        <div className="flex-1 px-4">
          <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest leading-tight">Check-In</p>
          <p className="text-[14px] font-bold text-gray-900 leading-tight whitespace-nowrap">
            {displayStart}
          </p>
        </div>
        <div className="flex-1 px-4 border-l border-gray-100">
          <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest leading-tight">Check-Out</p>
          <p className="text-[14px] font-bold text-gray-900 leading-tight whitespace-nowrap">
            {displayEnd}
          </p>
        </div>
      </div>
    );
  });

  LuxuryInput.displayName = 'LuxuryInput';

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .unavailable-date {
        position: relative;
        color: #ef4444 !important;
        text-decoration: line-through;
        opacity: 0.7;
      }
      .unavailable-date:hover {
        background-color: #fee2e2 !important;
      }
      .react-datepicker__day--unavailable {
        background-color: #fef2f2;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div className="custom-datepicker-container w-full">
      <DatePicker
        selectsRange={true}
        startDate={state.searchState.checkin}
        endDate={state.searchState.checkout}
        onChange={handleDateChange}
        onCalendarOpen={handleCalendarOpen}
        onCalendarClose={handleCalendarClose}
        onMonthChange={handleMonthChange}
        monthsShown={isMobile ? 1 : 2}
        minDate={getMinDate()}
        dateFormat="dd MMM yy"
        customInput={<LuxuryInput />}
        shouldCloseOnSelect={true}
        popperPlacement={isMobile ? "bottom" : "bottom-start"}
        wrapperClassName="w-full"
        renderDayContents={renderDayContents}
        dayClassName={date => {
          const dateStr = formatDateForAPI(date);
          const status = inventoryStatus[dateStr];
          return status && !status.has_available_inventory ? 'unavailable-date' : '';
        }}
      />
    </div>
  );
};

export default CustomDatePicker;
